<template>
  <div>
    <h2 class="vcr-page__header">
      {{ i18n.t('/Vcr/Configuration Details/Title') }}
    </h2>

    <h3 v-if="!isStockCar" class="vcr-page__sub-header">
      {{ i18n.t('/Vcr/Configuration Details/Configuration Code Label') }}
      <span data-qa="vcr-configuration-code">{{ configuration.ConfigurationCode }}</span>
    </h3>
    <h3 v-else class="vcr-page__sub-header">
      Kom-Number: <span data-qa="vcr-kom-number">{{ komNumber }}</span>
    </h3>

    <h4 data-qa="vcr-model-name" class="vcr-configuration-details__table-title">
      {{ configuration.ModelName }} {{ configuration.TrimlineName }}
    </h4>

    <table>
      <thead>
        <tr>
          <th class="vcr-page__table-first-column">
            {{ i18n.t('/Vcr/Configuration Details/Part Label') }}
          </th>
          <th>{{ i18n.t('/Vcr/Configuration Details/Code Label') }}</th>
          <th>{{ i18n.t('/Vcr/Configuration Details/Buy Label') }}</th>
          <th>{{ i18n.t('/Vcr/Configuration Details/Sell Label') }}</th>
          <th>{{ i18n.t('/Vcr/Configuration Details/Margin Label') }}</th>
          <th>{{ i18n.t('/Vcr/Configuration Details/Percentage Label') }}</th>
        </tr>
      </thead>

      <tbody>
        <tr>
          <th colspan="6">
            {{ i18n.t('/Vcr/Configuration Details/Base Car Label') }}
          </th>
        </tr>

        <tr>
          <td colspan="2">
            <div class="vcr-configuration-details__configuration-table-base">
              <span>{{ configuration.CatalogCarName }}</span>

              <small>
                {{ i18n.t('/Vcr/Configuration Details/Bpm Label') }}
                {{ i18n.n(configuration.NetCatalogPrice.BPM, 'currency') }}
              </small>
            </div>
          </td>
          <td>
            {{ i18n.n(configuration.NetCatalogPrice.PurchaseExcl, 'currency') }}
          </td>
          <td>{{ i18n.n(configuration.NetCatalogPrice.Excl, 'currency') }}</td>
          <td>
            {{ CurrencyHelper.margin(configuration.NetCatalogPrice.MarginExcl) }}
          </td>
          <td>
            {{ CurrencyHelper.marginPercentage(configuration.NetCatalogPrice.MarginPercentage) }}
          </td>
        </tr>

        <!-- eslint-disable vue/no-v-for-template-key -->

        <template v-for="(configurationOption, index) in configurationOptions" :key="index">
          <tr class="vcr-configuration-details__option-name">
            <th colspan="6">{{ configurationOption.Name }}</th>
          </tr>

          <tr>
            <td>{{ configurationOption.Body }}</td>
            <td>{{ configurationOption.ShortId }}</td>
            <td>{{ i18n.n(configurationOption.Buy, 'currency') }}</td>
            <td>{{ i18n.n(configurationOption.Sell, 'currency') }}</td>
            <td>{{ CurrencyHelper.margin(configurationOption.Margin) }}</td>
            <td>{{ CurrencyHelper.marginPercentage(configurationOption.MarginPercentage) }}</td>
          </tr>
        </template>

        <template v-for="category in configuration.Categories" :key="category.Id">
          <tr>
            <th colspan="6">{{ category.Name }}</th>
          </tr>

          <tr
            v-for="(configurationOption, index2) in category.Features"
            :key="index2 + category.Id + '_secondary'"
          >
            <td>{{ configurationOption.Name }}</td>
            <td>{{ configurationOption.ShortId }}</td>
            <td>
              {{ i18n.n(configurationOption.Price.PurchaseExcl, 'currency') }}
            </td>
            <td>{{ i18n.n(configurationOption.Price.Excl, 'currency') }}</td>
            <td>
              {{ CurrencyHelper.margin(configurationOption.Price.MarginExcl) }}
            </td>
            <td>
              {{ CurrencyHelper.marginPercentage(configurationOption.Price.MarginPercentage) }}
            </td>
          </tr>
        </template>
        <tr class="vcr-configuration-details__configuration-table-footer">
          <td colspan="2">
            {{ i18n.t('/Vcr/Configuration Details/Total Label') }}
          </td>
          <td>{{ i18n.n(configuration.Price.PurchaseExcl, 'currency') }}</td>
          <td>{{ i18n.n(configuration.Price.Excl, 'currency') }}</td>
          <td>{{ CurrencyHelper.margin(configuration.Price.MarginExcl) }}</td>
          <td>{{ CurrencyHelper.marginPercentage(configuration.Price.MarginPercentage) }}</td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import CurrencyHelper from '@/helpers/currencyHelper'

export default {
  data() {
    return {
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
      CurrencyHelper: CurrencyHelper,
    }
  },
  computed: {
    ...mapGetters({
      configurationExtendedGetter: 'configurationExtended/configurationExtended',
      quoteGetter: 'quote/get',
      isStockCar: 'quote/isStockCar',
      komNumber: 'configurationExtended/komNumber',
    }),
    configuration() {
      return (
        this.configurationExtendedGetter || {
          // empty configuration object
          NetCatalogPrice: {},
          Price: {},
        }
      )
    },
    quote() {
      return this.quoteGetter || {}
    },
    configurationOptions() {
      return [
        {
          Name: 'Exterieur',
          Body: `Kleur: ${this.configuration?.Color?.Name}`,
          ShortId: this.configuration?.Color?.ShortId,
          Buy: this.configuration?.Color?.Price?.PurchaseExcl,
          Sell: this.configuration?.Color?.Price?.Excl,
          Margin: this.configuration?.Color?.Price?.MarginExcl,
          MarginPercentage: this.configuration?.Color?.Price?.MarginPercentage,
        },
        {
          Name: 'Velgen',
          Body: `Velgen: ${this.configuration?.Rim?.Name}`,
          ShortId: this.configuration?.Rim?.ShortId,
          Buy: this.configuration?.Rim?.Price?.PurchaseExcl,
          Sell: this.configuration?.Rim?.Price?.Excl,
          Margin: this.configuration?.Rim?.Price?.MarginExcl,
          MarginPercentage: this.configuration?.Rim?.Price?.MarginPercentage,
        },
        {
          Name: 'Bekleding',
          Body: `Bekleding: ${this.configuration?.Interior?.Upholstery?.Name}`,
          ShortId: this.configuration?.Interior?.Upholstery?.ShortId,
          Buy: this.configuration?.Interior?.Upholstery?.Price?.PurchaseExcl,
          Sell: this.configuration?.Interior?.Upholstery?.Price?.Excl,
          Margin: this.configuration?.Interior?.Upholstery?.Price?.MarginExcl,
          MarginPercentage: this.configuration?.Interior?.Upholstery?.Price?.MarginPercentage,
        },
        {
          Name: 'Kleur',
          Body: `Kleur: ${this.configuration?.Interior?.UpholsteryColor?.Name}`,
          ShortId: this.configuration?.Interior?.UpholsteryColor?.ShortId,
          Buy: this.configuration?.Interior?.UpholsteryColor?.Price?.PurchaseExcl,
          Sell: this.configuration?.Interior?.UpholsteryColor?.Price?.Excl,
          Margin: this.configuration?.Interior?.UpholsteryColor?.Price?.MarginExcl,
          MarginPercentage: this.configuration?.Interior?.UpholsteryColor?.Price?.MarginPercentage,
        },
      ]
    },
  },
}
</script>

<style lang="scss" scoped>
.vcr-configuration-details {
  &__table-title {
    font-size: 1.3em;
    font-weight: 400;
  }

  &__configuration-table-base {
    display: flex;
    flex-direction: column;

    > small {
      font-size: 1em;
      font-weight: bold;
      margin-top: 1.5em;
    }
  }

  &__configuration-table-footer {
    font-weight: bold;
    background-color: #f1f4f5;
  }
}
</style>
